/**
 * Capture, and the author's own notes for one match — specs 02 and 03, thinnest coherent
 * slice.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN IS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * One observation about one named umpire, in as few taps as an online build can honestly
 * offer: pick the umpire, tap one or two of the **seven fixed tap-tags** (UM-Q9, shipped as
 * constants in `packages/shared`), or type a line, and press. Below it, the author's own
 * notes on this match — drafts, released and private, distinguished as spec 03 §2.3
 * distinguishes them — and the Release press that is the privacy boundary.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DELIBERATE DEVIATIONS, EACH WITH ITS REASON
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **There is a save press; a tag tap does not save by itself.** Spec 02 §2.4 wants the
 *     ≈3-second path where "a tag tap immediately saves a note". The press stays for now —
 *     the coalescing window that lets two chips join one note is what needs designing away,
 *     not the storage. The storage question §2.5 raised IS now answered: `Saved` is truthful
 *     because the note is committed to the phone's queue before this screen says anything,
 *     and a note that has not reached the server says `Waiting To Send` instead.
 *
 *  1b. **Offline, capture is complete and honest** (spec 10). A note captured with no signal
 *     is durable on the phone, renders in the list below from the queue itself, and carries
 *     the pending mark of spec 10 §4 until the server acknowledges it. Lock and Release queue
 *     the same way, and a queued release keeps its note in the `Draft` section under its own
 *     `Release Queued` mark — because "I pressed Release and it is queued" and "I have not
 *     pressed Release" must never look the same (§4).
 *  2. **The umpire is chosen from a dropdown, not from four photo tiles.** The tiles are spec
 *     01 §2.5's full design and are not built. What IS built (Alfred, 2026-09-09) is the
 *     narrowing behind them: when a match has real TMS appointments the dropdown offers only
 *     that match's appointed umpires — the two field umpires, the Reserve Umpire and the Video
 *     Umpire, each labelled with its role in plain words — and when it has none, which is
 *     today's live reality, the full Umpire roster shows exactly as it always has. The read is
 *     `GET /um/matches/:matchId/umpires` and it serves the REAL appointments alone: the TD's AI
 *     drafting reference set is his private planning data and never reaches this screen.
 *     Nothing here invents an appointment, and a failed read falls back to the full roster
 *     rather than blocking capture.
 *  3. **The minute stamp is provisional, and since RUN 85 it can arrive pre-filled from the TMS
 *     clock.** The live clock IS relayed now (spec 00 §7.3, UM-Q1): the TD's desktop session
 *     reads TMS's public match pages every two minutes and writes each live match's cumulative
 *     minute into `fixture.clock_minute` (migration 063; `control-centre/13-live-clock-relay.md`).
 *     When a note starts, this screen reads its match (`GET /fixtures/:id`), and while TMS reports
 *     the match live and not yet at full time, and the reading is no more than three minutes old
 *     BY THE SERVER'S CLOCK, the quarter and the minute are filled in with a line saying so and how
 *     old the reading is - an age that keeps counting while the screen stays open, until the
 *     reading is withdrawn at three minutes. It is a convenience and nothing more: the
 *     relay lags TMS by up to about two minutes, TMS's minute is whatever the technical table last
 *     pushed, both fields stay fully editable, and a field the manager goes to is theirs for that
 *     note - no reading changes it, and the next note starts from the clock again. A stale, absent
 *     or unreadable reading, a match not in play, and a read that fails, is slow, never answers
 *     (it is abandoned at ten seconds) or cannot be made offline all leave the fields as they were
 *     before the relay existed, and capture never waits for the read. Every stamp still carries
 *     `minuteProvisional: true`, pre-filled or typed - spec 02 §2.7's fallback - because the
 *     correction path that would set it false is not built, and a relayed minute is no more a
 *     record than a typed one. `StampFields` below carries the design, and the two reviews of
 *     19 Sep 2026 that shaped it.
 *  4. **No `Undo` snackbar, no `Move To Another Umpire` and no pending marks.** Each belongs to
 *     a later slice; none is stubbed, because a control that looked like it worked would be
 *     worse than one that is absent.
 *
 *  5. **Native-language capture IS built** (run 60, spec 08). `Write In Another Language` is the
 *     composer affordance spec 08 §3 asks for — *"a UM whose Preferred Language is English may
 *     still switch a single note to native capture from the composer's overflow"* — and a note
 *     captured through it opens into `NativeNoteReview`, the side-by-side review surface, which
 *     is where its English is made, corrected and released. A native capture carries NO English:
 *     the English is Call 2's answer, in a later request, which is what makes the capture itself
 *     as complete and as durable offline as an English one.
 *
 *  6. **An online save now confirms itself explicitly** (run 63; the TD's own finding in live
 *     UM-side testing, 2026-09-09 — *"I saved notes and I am not sure they are saved."*). The
 *     mark by the button reads `Saved ✓` with the note's stamp, stands until the next note is
 *     begun rather than expiring on a timer, and says whether the note was read back from
 *     `GET /notes/mine` or not. The `Saving` type below carries the whole design and its
 *     reasons. The queued mark is unchanged: it was already the honest one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE NOTE PRIVACY LIFECYCLE — RULING §H (D-5, 2026-08-12)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Three states, three labels, spelled exactly as Alfred ruled them: **Draft · Locked ·
 * Released**. They are section headings on this screen and the vocabulary of every control.
 *
 *  * **Capture always creates a Draft** — "private by default". There is NO capture-time
 *    `Keep Private` checkbox: it is gone from this component, from `CaptureNoteRequest` and
 *    from the API's accepted fields. A mis-tap at capture can no longer make anything
 *    permanent, which is the hazard §H consequence 1 names.
 *  * **Lock** is an explicit button on an existing Draft — the author's deliberate "this
 *    stays mine". One press, no confirmation: it is a considered act taken at the desk on a
 *    note already written, and it is not the end of the road — the note can still be
 *    released later, which is exactly why it needs no dialogue to guard it.
 *  * **Release on a Locked note warns first**, in §H's own words — *"The note is locked, do
 *    you want to unlock it and release it?"* — and Yes unlocks AND releases in one step. The
 *    warning is rendered IN THE PAGE FLOW beside the note it concerns (the house pattern,
 *    `PushConfirm`), never as `window.confirm`: a decision this size must not be dismissible
 *    by a stray click, and the note it is about must stay on screen while it is made.
 *  * **The gate locks nothing.** A Draft left unselected at the Release press simply stays a
 *    Draft, releasable on any later day. There is no time constraint and nothing back-dates.
 *  * **Released is permanent after fifteen minutes**, and for those fifteen minutes the author
 *    has one way back — `Undo Release`, on the released row itself (TD authorisation,
 *    9 September 2026; spec 11 §2's dated amendment). It is drawn QUIETLY: a small control
 *    beside the row with a plain countdown, `Undo Release · 14 Min Left`, never a warning
 *    colour and never a modal. A release is a normal act done correctly nearly every time, and
 *    a screen that treated it as a hazard would teach managers to hesitate over the one press
 *    the whole product depends on.
 *  * **The countdown is advisory and the screen says nothing it cannot back up.** The instant it
 *    counts to comes from the SERVER (`releasedAt`, and `undoUntil` on the press receipt), the
 *    server re-checks the window when the button is pressed, and a phone whose clock has drifted
 *    is simply told, in the flow, that the release is final. When the window closes the control
 *    disappears and the row reads exactly as a released row always has.
 *  * **The undo does not claim the note was unseen.** It was on the panel's and the TD's screens
 *    while it was released, and the copy never suggests otherwise: it says the note is back with
 *    its author, not that nothing happened.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EDITING A NOTE BEFORE RELEASE — SPEC 03 §2.3
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * *"Rows are tappable to expand and edit text before release. After release, notes are
 * append-only."* So a **Draft** row and a **Locked** row each carry an `Edit` button, and a
 * **Released** row carries none — not a disabled one, not a greyed one: a control that looks
 * like it might work on a released note would be offering to do the one thing this product
 * promises it will never do. A correction to a released note is a NEW note.
 *
 * Locked rows are editable for the same reason the API allows it: a lock says who may read a
 * note, never whether its author has finished writing it.
 *
 * **Save says nothing until the reply lands** (honesty rule 1, `UmReportComposer.tsx`). The
 * button reads `Saving…` while the call is in flight; the list is re-read from
 * `GET /notes/mine` afterwards, so what appears on screen is what the SERVER holds and not
 * what this phone sent. `Cancel` closes the editor and leaves the stored text exactly as it
 * was, because nothing was sent.
 *
 * **THE EDIT IS DELIBERATELY NOT QUEUED IN THIS SLICE, AND THE SCREEN SAYS SO WHEN IT
 * MATTERS.** Capture, Lock and Release all survive a dead venue wifi through the offline
 * queue; an edit does not. The queue's idempotency is written around capture's rule that a
 * replayed write LOSES to the stored note (spec 10 §2.3) — which is precisely backwards for an
 * edit, whose purpose is to overwrite what is stored — so queuing edits needs a per-note
 * ordering rule that does not exist yet, and a half-built one would replay a stale edit over a
 * newer one. Rather than pretend, a failed save shows an in-flow failure line **and keeps the
 * typed text in the textarea**, so nothing the author wrote is lost and nothing claims to have
 * been saved. When the queue gains that ordering rule, this is the one call to move onto it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS LOAD-BEARING AND MUST NOT BE SOFTENED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  * The locked-note copy is `Never Shared · Still Writes Your Report` and the word
 *    "encrypted" appears nowhere: the honest position is access control plus undertaking
 *    (spec 00 §3 C2, DATA-REVIEW G4).
 *  * There is no microphone, no recorder and no permission request (decision B3). The
 *    composer's own label names the phone keyboard's mic key and says no audio is recorded.
 *  * Every write goes through the client's single write seam (`api/client.ts`), never
 *    through a `fetch` of its own.
 */

import { useEffect, useRef, useState } from "react";

import {
  CAPTURE_TAP_TAGS,
  LIVE_CLOCK_FRESH_MS,
  LIVE_CLOCK_MAX_MINUTE,
  languageByCode,
  languageByName,
  liveClockAgeLabel,
  liveClockAgeLabelChangesInMs,
  liveClockPrefill,
  undoWindowIsOpen,
  undoWindowLabel,
  undoWindowRemainingMs,
  type LiveClockPrefill,
} from "@hockeytd/shared";

import {
  captureNote,
  deviceId,
  editNoteText,
  fetchFixture,
  fetchMatchUmpires,
  fetchMyNotes,
  fetchOfficials,
  fetchPreferredLanguage,
  isOffline,
  lockNote,
  newNoteId,
  releaseMatchNotes,
  setPreferredLanguage,
  unreleaseNote,
} from "../../api/client.js";
import type { QueuedCapture, QueueStatus } from "../../api/noteQueue.js";
import type { AppointedUmpireBody, OwnNoteView, ReleaseReceiptBody } from "../../api/types.js";
import { pulseElement } from "../../app/HowToUse.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { useQueueStatus } from "../../app/useQueueStatus.js";
import { NativeNoteReview } from "./NativeNoteReview.js";

export interface CapturePanelProps {
  matchId: number;
  competitionId: "1916" | "1917";
}

/**
 * The warning of ruling §H, **verbatim**. It is exported so a test asserts the shipped string
 * rather than a paraphrase of it, and so there is exactly one copy of it in the build.
 */
export const LOCKED_RELEASE_WARNING =
  "The note is locked, do you want to unlock it and release it?";

/**
 * The receipt a **queued** Release press gets — spec 03 §4 and spec 10 §5 step 5, in their
 * own words: `Release Queued — 4 Notes Will Go When You're Back Online`.
 *
 * Every word of it is in the future tense on purpose. Nothing has reached the panel: the
 * notes are still Drafts on the server and are still Drafts on this screen, under their own
 * `Release Queued` marks. A receipt that read `Released` here would be the single most
 * damaging sentence in the product — it is the trust mechanic reporting an outcome that has
 * not happened, on the one screen whose entire purpose is that boundary.
 *
 * Exported so the test asserts the shipped string rather than a paraphrase of it.
 */
export function releaseQueuedReceipt(count: number): string {
  return `Release Queued — ${String(count)} ${
    count === 1 ? "Note" : "Notes"
  } Will Go When You’re Back Online`;
}

/**
 * THE SAVE CONFIRMATION — the TD's own finding, live UM-side testing, 2026-09-09.
 *
 * *"I saved notes and I am not sure they are saved."* Until this run an ONLINE save confirmed
 * itself with `Saved · Auto-Stamped` and nothing else, and the proof — the note appearing in
 * `Your Notes On This Match` — is below the fold on a phone. The OFFLINE path was already the
 * honest one (`Saved On This Phone · Waiting To Send`), which is why a manager standing beside
 * a pitch with good wifi got the *weaker* assurance than one with none.
 *
 * Three properties of the design, each chosen against a specific failure:
 *
 *  * **`saved` carries the note's own id and its stamp.** The stamp is what tells the author
 *    WHICH note this confirms — they have just written four in two minutes — and the id is what
 *    lets this screen check its own claim against the re-read (below).
 *  * **It does not vanish on a timer.** A two-second flash is exactly what a gloved hand on a
 *    cold morning misses, and a confirmation nobody saw is worse than none: it teaches the
 *    author that the screen says nothing. It stands until the NEXT note is begun — the first
 *    keystroke, the first tag tap, a change of umpire or of stamp — because at that moment the
 *    author has stopped asking about the last note.
 *  * **It says whether it VERIFIED the note.** A 2xx means the server took the write; the
 *    re-read of `GET /notes/mine` is what proves it is stored and readable back. When that
 *    re-read has landed and the note is in it, the mark says so. When the re-read failed, or
 *    landed without the note, the mark still says `Saved ✓` — the receipt was real — and adds
 *    the honest instruction rather than an unearned `In Your Notes Below`.
 */
type Saving =
  | { status: "idle" }
  | { status: "saving" }
  /** ONLINE success. `noteId` is what the re-read is checked for; `stamp` is which note it was. */
  | { status: "saved"; noteId: string; stamp: string }
  /** Committed to the phone, not to the server. The two are never printed the same way. */
  | { status: "queued" }
  | { status: "failed"; error: Error };

/** What a Release press produced. A queued press has no receipt and never borrows one. */
type ReleaseResult =
  | { kind: "sent"; body: ReleaseReceiptBody }
  | { kind: "queued"; count: number };

/**
 * A queued capture, rendered as though it were a note — because it is one.
 *
 * Spec 10 §5 step 6 requires that a force-quit and re-open leaves "everything still there",
 * and §2.5 of spec 02 requires the preview to be the live note. The queue carries the whole
 * `CaptureNoteRequest`, so an unsent note needs no second local store to be visible; what it
 * needs is to be honest about what it is, and that is the pending mark, not this projection.
 *
 * The fields the server would have filled are `null` rather than guessed. `visibility` is
 * `draft` as a literal and not as an assumption: ruling §H makes capture create a Draft,
 * always, as a literal in the insert.
 */
function noteFromQueue(write: QueuedCapture, umpireMediaName: string | null): OwnNoteView {
  return {
    id: write.noteId,
    authorUmId: "",
    authorShortName: null,
    umpireId: write.note.umpireId,
    umpireMediaName,
    // The kind the queued write itself carries, or — when it carries none, which is every
    // capture this screen makes — the SAME derivation the API applies to an omitted kind
    // (`captureKind`, spec 13 §2). A projection that guessed differently from the server would
    // make a note change kind the moment the queue drained.
    kind:
      write.note.kind ?? (write.note.umpireId === null ? "general" : "observation"),
    competitionId: write.note.competition,
    matchId: write.note.matchId,
    matchNo: null,
    quarter: write.note.quarter,
    matchMinute: write.note.matchMinute,
    minuteProvisional: write.note.minuteProvisional,
    bodyEn: write.note.bodyEn,
    shortSummary: null,
    tagIds: [...write.note.tagIds],
    criterionStableKey: null,
    aiState: "none",
    capturedAt: write.note.capturedAt,
    visibility: "draft",
    releasedAt: null,
    bodyNative: null,
    nativeLang: null,
  };
}

/**
 * The per-note pending mark of spec 10 §4 — a glyph plus the line it means.
 *
 * Three marks, in this order of precedence, because they answer different questions and the
 * most consequential one wins:
 *
 *  * `Release Queued` — *"I pressed Release and it is queued"*, which §4 insists a UM must be
 *    able to tell apart from *"I have not pressed Release"*. It is the one that must never be
 *    hidden behind a more generic mark.
 *  * `Lock Queued` — the author's `Lock` has not landed yet.
 *  * `Waiting To Send` — §4's plain case: the note itself is not with the server.
 *
 * The line is rendered rather than revealed on tap. §4 allows the tap, but a phone-sized
 * glyph whose meaning is one tap away is a glyph most people never read, and this is the mark
 * that stands between a UM and believing a queued note was sent.
 */
function PendingMark({ noteId, queue }: { noteId: string; queue: QueueStatus }): React.JSX.Element | null {
  if (queue.queuedReleaseNoteIds.includes(noteId)) {
    return <span className="pendmark pendmark--release">◍ Release Queued</span>;
  }
  if (queue.queuedLockNoteIds.includes(noteId)) {
    return <span className="pendmark">◌ Lock Queued</span>;
  }
  if (queue.pendingNoteIds.includes(noteId)) {
    return <span className="pendmark">◌ Waiting To Send</span>;
  }
  return null;
}

/**
 * `Q2 · 22′` from the three fields that make it, so the save confirmation can print the stamp
 * of the note it has just written — which exists as a quarter and a minute on this screen
 * before any row exists to read it off.
 *
 * `provisional` appends the trailing `·` that marks a typed stamp on a note row. The
 * confirmation passes `false`: the glyph is a footnote marker beside a stamp in a list, and at
 * the end of a sentence it reads as a stray character rather than as a qualification. The line
 * under the stamp fields already says the stamp is a memory-jogger.
 */
function stampOf(
  quarter: number | null,
  matchMinute: number | null,
  provisional: boolean,
): string {
  if (quarter === null && matchMinute === null) return "Unstamped";
  const quarterPart = quarter === null ? "" : `Q${String(quarter)}`;
  const minutePart = matchMinute === null ? "" : `${String(matchMinute)}′`;
  return `${[quarterPart, minutePart].filter((part) => part !== "").join(" · ")}${
    provisional ? " ·" : ""
  }`;
}

function stamp(note: OwnNoteView): string {
  return stampOf(note.quarter, note.matchMinute, note.minuteProvisional);
}

/**
 * RUN 85 - THE TWO STAMP FIELDS, AND WHERE WHAT IS IN THEM CAME FROM.
 *
 * The quarter and the minute can hold three kinds of value, and the screen has to know which,
 * because each is treated differently from then on:
 *
 *  * `blank` - nothing. Where every note began before the relay existed.
 *  * `typed` - the manager's own. Carried into the next note exactly as a typed stamp always was
 *    ("the next observation is usually about the same umpire, moments later") until a usable
 *    reading for that note replaces it: the next note starts from the clock.
 *  * `clock` - a TMS reading, offered. Shown only while it answers the latest read AND is no more
 *    than three minutes old by the most conservative measure available (`clockAgeMs`), and
 *    withdrawn - locally, with no network - the moment either stops being true.
 *
 * `touched` overrides the first of those: once the manager has GONE TO a stamp field during a
 * note - focused it or edited it - no reading overwrites the stamp and no ask withdraws it for the
 * rest of that note. It does NOT override the second. A reading the clock filled lapses at three
 * minutes whether or not the manager has tapped into it (the frontend re-review: a value focused
 * while a note was saving was carried into the next note and saved at 4.5 minutes old), both on
 * screen and at the save - so the fields never show a stamp the save would drop. An edit makes the
 * stamp `typed`, and a typed stamp never lapses.
 *
 * THE FINDINGS THIS SHAPE ANSWERS (the RUN 85 backend and frontend reviews, 19 Sep 2026):
 *
 *  * **Every ask withdraws first** (`withdrawClockStamp`). Save never waits for a read - a tag
 *    alone is a complete note - and as first built the last reading stayed in the fields while
 *    the next note's read was out, so a note saved in that gap went out with the previous reading
 *    under a line calling it fresh (Q2 22 saved while TMS was on Q3 38).
 *  * **The age keeps counting on an open screen.** The state holds the reading's age when it
 *    ARRIVED and this phone's instant of arrival; every render shows the age it is now, a timer
 *    redraws whenever the label would change, and the reading is withdrawn at the three-minute
 *    mark. A label drawn once had frozen at "3 Min Ago", and the reading was then saved minutes
 *    later.
 *  * **A read has a real deadline** (`CLOCK_READ_PATIENCE_MS`): a timer gives up on it and aborts
 *    the request, instead of a check that ran only if an answer ever came.
 *  * **Going to a field holds it.** A reading that landed while the Minute field had focus turned
 *    the manager's "20" into "2220"; a focus now marks the stamp `touched`.
 *  * **A save's reply resets only what the manager has not touched since the press** (`save`).
 *
 * **When the clock is read** - when a note starts, and never on a timer: when this screen opens,
 * when a note has just been saved, when the phone comes back to this page before the next note
 * has begun, and at a note's first touch - a tag, a keystroke, a change of umpire - because that
 * is the moment being stamped. Each read supersedes the one before it, and a superseded read is
 * aborted, not merely ignored. The only timers are local: the read's deadline and the redraw.
 */
interface StampFields {
  /** `""` or `"1"`..`"4"`, as the select holds it. */
  quarter: string;
  /** `""` or the digits the input holds. */
  minute: string;
  source: "blank" | "typed" | "clock";
  /**
   * `clock` only. The reading's age when it ARRIVED here: the server's `serverNow` less the
   * relay's stamp, plus the whole round trip of the read - an upper bound on the time since the
   * server answered, so the age is never under-counted.
   */
  clockAgeAtArrivalMs: number | null;
  /** `clock` only. This phone's instant of arrival, on both of its clocks. */
  clockArrivedAt: LocalInstant | null;
  /** The manager has gone to a stamp field during THIS note - focused it or edited it. */
  touched: boolean;
}

const BLANK_STAMP: StampFields = {
  quarter: "",
  minute: "",
  source: "blank",
  clockAgeAtArrivalMs: null,
  clockArrivedAt: null,
  touched: false,
};

/**
 * This phone's two clocks, read together - for DURATIONS on this phone and nothing else. A
 * reading's age is the server's age (the server's clock against itself) plus durations measured
 * here (the phone's clocks against themselves); no phone instant is ever compared with a server
 * instant.
 */
interface LocalInstant {
  mono: number;
  wall: number;
}

function localNow(): LocalInstant {
  return { mono: performance.now(), wall: Date.now() };
}

/**
 * How long ago `then` was, on this phone: the LONGER of what its two clocks say, never negative.
 * The monotonic clock ignores anybody setting the time, but on some phones it stands still while
 * the phone sleeps; the wall clock runs through sleep but can be set. Whichever of them fails,
 * the longer answer errs towards calling a reading older - never fresher.
 */
function elapsedMs(then: LocalInstant, now: LocalInstant): number {
  return Math.max(0, now.mono - then.mono, now.wall - then.wall);
}

/** A clock stamp's age NOW: its age at arrival plus the time since. `null` for any other stamp. */
function clockAgeMs(stamp: StampFields, now: LocalInstant): number | null {
  if (
    stamp.source !== "clock" ||
    stamp.clockAgeAtArrivalMs === null ||
    stamp.clockArrivedAt === null
  ) {
    return null;
  }
  return stamp.clockAgeAtArrivalMs + elapsedMs(stamp.clockArrivedAt, now);
}

/** The manager's own edit of either field. It is theirs for the rest of this note. */
function typedStamp(
  current: StampFields,
  change: { quarter?: string; minute?: string },
): StampFields {
  return {
    quarter: change.quarter ?? current.quarter,
    minute: change.minute ?? current.minute,
    source: "typed",
    clockAgeAtArrivalMs: null,
    clockArrivedAt: null,
    touched: true,
  };
}

/**
 * The clock's values, taken back: what every ask of the clock does FIRST, in the same batch of
 * updates as the ask, so no render shows them once a newer reading has been asked for. Only an
 * untouched clock stamp is withdrawn; a typed one, and anything the manager has gone to, stays -
 * though a clock value still lapses at three minutes, held or not (`expireClockStamp`).
 */
function withdrawClockStamp(current: StampFields): StampFields {
  return current.source === "clock" && !current.touched ? BLANK_STAMP : current;
}

/**
 * A clock stamp past its three minutes, withdrawn - locally, with no network - whether or not the
 * manager has tapped into it (`StampFields`). A typed stamp has no age and never lapses.
 */
function expireClockStamp(current: StampFields, now: LocalInstant): StampFields {
  const age = clockAgeMs(current, now);
  return age !== null && age > LIVE_CLOCK_FRESH_MS ? BLANK_STAMP : current;
}

/** What one read brought back, when it is usable. */
interface ClockAnswer {
  prefill: LiveClockPrefill;
  roundTripMs: number;
  arrivedAt: LocalInstant;
}

/**
 * A settled read: a usable answer, or `null` for every reason there is none - stale, absent, not
 * in play, failed, slow, abandoned at the deadline, offline. A stamp the manager has gone to is
 * left alone whatever arrives; a usable answer fills both fields (the quarter blank when the
 * period names none); anything else withdraws what the clock put there and leaves a typed stamp.
 */
function stampAfterReading(current: StampFields, answer: ClockAnswer | null): StampFields {
  if (current.touched) return current;
  if (answer === null) return withdrawClockStamp(current);
  return {
    quarter: answer.prefill.quarter === null ? "" : String(answer.prefill.quarter),
    minute: String(answer.prefill.minute),
    source: "clock",
    clockAgeAtArrivalMs: answer.prefill.ageMs + answer.roundTripMs,
    clockArrivedAt: answer.arrivedAt,
    touched: false,
  };
}

/** A note boundary: the next note begins untouched, so the clock may fill it again. */
function stampForNextNote(current: StampFields): StampFields {
  return current.touched ? { ...current, touched: false } : current;
}

/** The manager has gone to a stamp field: whatever is there now is held for this note. */
function heldStamp(current: StampFields, now: LocalInstant): StampFields {
  // An expired reading is withdrawn first, so going to the field can never adopt one.
  const live = expireClockStamp(current, now);
  return live.touched ? live : { ...live, touched: true };
}

/**
 * How long a clock read may take and still be used: **ten seconds**, on this phone's clocks (a
 * duration). A timer gives up on the read at that moment and aborts its request - a read that
 * never answers is abandoned, not waited for - and an answer that still arrives later (a phone
 * that slept through the timer) is refused on arrival. Nothing waits for the read either way:
 * `Save This Note` never looks at it.
 */
export const CLOCK_READ_PATIENCE_MS = 10_000;

/**
 * The line under the stamp fields while they hold a reading - `From TMS Clock · Read 2 Min Ago`,
 * or `From TMS Clock · Read Under 1 Min Ago` - drawn as two parts. "Read" dates the reading, so the
 * manager can add the minutes the relay has not caught up with.
 *
 * SHORT, AND ONE LINE AT ANY WIDTH (the frontend re-review): the first wording, `From The TMS Clock
 * · Read Less Than 1 Min Ago`, was ~306 px of text in ~298 px at a 360 px phone, so it wrapped and
 * moved `Save This Note` 20 px as a reading came and again when the label turned at a minute. The
 * line is now at most ~253 px at 13 px, its slot is exactly one line tall, and when it still does
 * not fit - larger system text, a 320 px phone - `CLOCK_SOURCE_FROM` gives way with an ellipsis
 * and the age, which is what the manager acts on, never does. The whole sentence stays in the DOM,
 * so a screen reader hears all of it. Named, for the house rule on strings a test asserts.
 */
export const CLOCK_SOURCE_FROM = "From TMS Clock";

/** The part of the line that is never clipped: `· Read 2 Min Ago`. */
export function clockSourceAge(ageMs: number): string {
  return `· Read ${liveClockAgeLabel(ageMs)}`;
}

/** The whole line, as a screen reader hears it and a test asserts it. */
export function clockSourceLine(ageMs: number): string {
  return `${CLOCK_SOURCE_FROM} ${clockSourceAge(ageMs)}`;
}

/** The line's id, so a screen reader reads it with the two fields it describes. */
const STAMP_SOURCE_ID = "capture-stamp-source";

/**
 * A minute a note may not carry: anything but blank or a whole number from 0 to 120 - the range
 * migration 063 gives TMS's own clock. Refused at the Save button with a sentence saying so, rather
 * than sent for the API to refuse (it takes no fraction) or to store (it has no ceiling).
 */
function minuteIsRefused(minute: string): boolean {
  if (minute === "") return false;
  return !/^\d{1,3}$/.test(minute) || Number(minute) > LIVE_CLOCK_MAX_MINUTE;
}

/** The refusal's id, so the Minute field names it as its error and its description. */
const MINUTE_REFUSED_ID = "capture-minute-refused";

/** Space-separated ids for `aria-describedby`, or nothing when there are none. */
function describedBy(...ids: (string | null)[]): string | undefined {
  const present = ids.filter((id): id is string => id !== null);
  return present.length === 0 ? undefined : present.join(" ");
}

/** The sentence beside a refused minute. Named, for a test to assert word for word. */
export const MINUTE_REFUSED =
  "The Minute Must Be A Whole Number From 0 To 120 — Correct It, Or Clear It To Save The Note Without One.";

/** Two tag selections are the same selection. */
function sameIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/** The label a tag id renders as. The palette and the API's validator share one constant. */
function tagLabel(tagId: string): string {
  return CAPTURE_TAP_TAGS.find((tag) => tag.tag_id === tagId)?.label ?? tagId;
}

/**
 * RUN 78 — the note a `#note-<id>` fragment names, or `null` for anything else.
 *
 * `Open In Match ›`, in an opened note on My Notes, links here with the note's own id in the
 * fragment, and the screen lands on that row. The pattern is the full UUID shape rather than
 * "anything after the dash" on purpose: the fragment is turned straight into an element id
 * lookup, and a hash a manager has typed, mangled or shared should fall out here as `null`
 * rather than reach the DOM. Everything about the screen that is not this landing is
 * unaffected either way — a hash that names no note of this match is simply nothing to do.
 */
export function noteIdFromHash(hash: string): string | null {
  const found =
    /^#note-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.exec(hash);
  return found?.[1] ?? null;
}

/**
 * The four slot codes, in plain Technical-Delegate language.
 *
 * `U1` and `VU` are what the database stores and what appointment reports print; they are not
 * words a manager should have to decode at 09:00 beside a pitch. The mapping lives HERE rather
 * than on the API response so that the wording can be changed without a deployment of the
 * server, and so that the API carries the stored code and nothing that reads like a decision
 * about how a screen should speak.
 */
const SLOT_LABEL: Readonly<Record<AppointedUmpireBody["slot"], string>> = {
  U1: "Umpire 1",
  U2: "Umpire 2",
  RU: "Reserve Umpire",
  VU: "Video Umpire",
};

/**
 * One option in the narrowed dropdown: the name, the country as everywhere else on this screen,
 * and the role the person is actually doing in this match.
 */
function appointedLabel(umpire: AppointedUmpireBody): string {
  return `${umpire.name} · ${umpire.country} — ${SLOT_LABEL[umpire.slot]}`;
}

/**
 * The one sentence the release receipt says about the undo, and the test's own handle on it.
 *
 * It is named rather than inlined so a copy change is one edit in one place — the house rule for
 * every string on this screen that a test asserts word for word.
 */
export const UNDO_RECEIPT_SUFFIX = " · You Can Undo This For 15 Minutes";

/**
 * Whether the receipt should still name the undo.
 *
 * **It reads `undoUntil`, which the SERVER computed from the stamp the server wrote**, rather
 * than adding fifteen minutes to anything on this phone. `null` means the press released
 * nothing and there is nothing to take back; a deadline already passed means the receipt is
 * still on screen but the offer has expired, and a receipt that went on offering it would be
 * the screen promising something the API would refuse.
 */
export function undoStillOffered(undoUntil: string | null, now: number): boolean {
  if (undoUntil === null) return false;
  const at = Date.parse(undoUntil);
  return !Number.isNaN(at) && at > now;
}

export function CapturePanel({ matchId, competitionId }: CapturePanelProps): React.JSX.Element {
  const [reloads, setReloads] = useState(0);

  const roster = useAsync(() => fetchOfficials(), []);
  /**
   * WHO IS ON THIS MATCH — decided by Alfred (the TD) on 2026-09-09, built ahead of the data.
   *
   * When TMS holds real appointments for this match, the Umpire dropdown below offers those
   * umpires and nobody else: the two field umpires, the Reserve Umpire and the Video Umpire.
   * That is the whole honest list, and scrolling past forty names to find one of four is a tax
   * this screen should not charge a manager standing beside a pitch.
   *
   * **An empty answer and a failed answer are different things, and both end in the full
   * roster.** Empty means *"nobody has appointed anyone"* — today's live reality, since this
   * system holds no umpire appointments yet — and the screen behaves exactly as it did before
   * this read existed. A FAILED read means we do not know, and the fallback is the same full
   * roster for the reason every other degradation in this app takes the generous branch: an
   * outage must never be the thing that stops a note being written.
   *
   * The read is per match and re-runs when the match changes. It carries nothing about the
   * caller, because the four names are the same for every manager at that pitch.
   */
  const appointed = useAsync(() => fetchMatchUmpires(matchId), [matchId]);
  const notes = useAsync(() => fetchMyNotes({ match: matchId }), [matchId, reloads]);
  /**
   * Decision L2's field (spec 08 §1), read once. It supplies the composer's default language and
   * the picker's options, and it is the caller's own — the route takes no parameter and there is
   * no panel-wide list anywhere in this API.
   *
   * A failed read is NOT a failure of this screen: capture works, in English, exactly as it did
   * before run 60. The native affordance simply does not appear, because a picker with no
   * options is a control that cannot be used.
   */
  const [languageReloads, setLanguageReloads] = useState(0);
  const languages = useAsync(() => fetchPreferredLanguage(), [languageReloads]);

  const [umpireId, setUmpireId] = useState<string>("");
  const [bodyEn, setBodyEn] = useState("");
  /**
   * Spec 08 §3's per-note switch. `null` is the ordinary English composer; a language code is a
   * native capture, and the composer then takes the ORIGINAL rather than English.
   *
   * It is per note and not per session on purpose: §3 gives a bilingual manager both paths, and
   * a switch that stuck would silently file the next English note as a native one.
   */
  const [nativeLang, setNativeLang] = useState<string | null>(null);
  const [bodyNative, setBodyNative] = useState("");
  /** The note whose side-by-side review surface is open, if any. One at a time, in the flow. */
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [languageSaving, setLanguageSaving] = useState(false);
  const [tagIds, setTagIds] = useState<readonly string[]>([]);
  /** RUN 85 - the quarter and the minute, and where they came from. See `StampFields`. */
  const [stampFields, setStampFields] = useState<StampFields>(BLANK_STAMP);
  const quarter = stampFields.quarter;
  const matchMinute = stampFields.minute;
  /**
   * How old the reading in the fields is NOW - worked out at every render from its age at arrival
   * and the time since, so it is never the number it was when it landed. `null` unless the fields
   * hold a reading. The redraw timer below makes sure a render happens whenever the label turns.
   */
  const clockAgeNow = clockAgeMs(stampFields, localNow());
  const minuteRefused = minuteIsRefused(matchMinute);
  const [saving, setSaving] = useState<Saving>({ status: "idle" });

  /**
   * RUN 85 - THE CLOCK READ, one per ask. `clockAsk` is bumped at each moment `StampFields`
   * names; `noteBegun` says whether the current note has had its first touch yet.
   *
   * It is a read like `appointed` above and degrades the same way, towards capture: offline it is
   * not even attempted, and a failed, slow or unanswered read is simply no reading. Nothing on this
   * screen waits for it. A reading's age is the SERVER's `serverNow` less the relay's stamp - both
   * server instants - plus durations measured on this phone (`LocalInstant`).
   */
  const [clockAsk, setClockAsk] = useState(0);
  const noteBegun = useRef(false);
  /**
   * The ONE way the clock is asked, from an event: a note's first touch, a return to the page, a
   * saved note. It withdraws the clock's values in the same batch as the ask, so until this read
   * answers the fields hold a typed stamp, a held one, or nothing (`withdrawClockStamp`).
   */
  const askClock = (): void => {
    setStampFields(withdrawClockStamp);
    setClockAsk((count) => count + 1);
  };

  /**
   * THE MANAGER'S EDITS, COUNTED (the RUN 85 frontend review). `any` counts every edit of the
   * composer - the words, a tag, the umpire, either stamp field - and `stamp` the stamp fields
   * alone, a focus included (the manager has gone to the field). A save notes both at its press
   * and compares them in its reply, which is how a reply landing seconds later can tell what the
   * manager has done since and leave it alone (`save`).
   */
  const composerEdits = useRef({ any: 0, stamp: 0 });
  const countEdit = (ofStamp: boolean): void => {
    composerEdits.current.any += 1;
    if (ofStamp) composerEdits.current.stamp += 1;
  };

  /**
   * A different match is a different note: an edit made on the last one stops sticking, and a
   * value the clock filled for the LAST match is withdrawn - the read below then asks for this
   * one. Adjusted DURING render, React's pattern for state that follows a prop, rather than in an
   * effect: an effect runs after the render it follows, and that render would have paired this
   * match with another match's clock for as long as a tap takes.
   */
  const [stampMatchId, setStampMatchId] = useState(matchId);
  if (stampMatchId !== matchId) {
    setStampMatchId(matchId);
    setStampFields((current) => withdrawClockStamp(stampForNextNote(current)));
  }
  // The ref is not render state and may not be written during render; only event handlers read it.
  useEffect(() => {
    noteBegun.current = false;
  }, [matchId]);

  useEffect(() => {
    // Open until this read settles, a newer ask supersedes it, or the match changes.
    let open = true;
    const askedAt = localNow();
    const controller = new AbortController();
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const close = (): void => {
      open = false;
      if (deadline !== undefined) clearTimeout(deadline);
      // A read nobody waits for any more gives its connection back. A no-op once it has answered.
      controller.abort();
    };
    const settle = (prefill: LiveClockPrefill | null): void => {
      if (!open) return;
      close();
      const arrivedAt = localNow();
      const roundTripMs = elapsedMs(askedAt, arrivedAt);
      setStampFields((current) =>
        stampAfterReading(
          current,
          prefill !== null && roundTripMs <= CLOCK_READ_PATIENCE_MS
            ? { prefill, roundTripMs, arrivedAt }
            : null,
        ),
      );
    };
    if (isOffline()) {
      // No signal: not even attempted, exactly as the note queue does not attempt a send.
      settle(null);
    } else {
      // THE DEADLINE, as a timer: a read that never answers is given up on at ten seconds, and
      // its request aborted - it does not wait for an answer that may never come.
      deadline = setTimeout(() => {
        settle(null);
      }, CLOCK_READ_PATIENCE_MS);
      fetchFixture(matchId, { signal: controller.signal }).then(
        (fixture) => {
          settle(
            liveClockPrefill({
              tmsStatus: fixture.tmsStatus,
              period: fixture.period,
              clockMinute: fixture.clockMinute,
              clockUpdatedAt: fixture.clockUpdatedAt,
              // THE SERVER'S instant, from the same answer - never `new Date()` on this phone.
              serverNow: fixture.serverNow,
            }),
          );
        },
        () => {
          settle(null);
        },
      );
    }
    return close;
  }, [matchId, clockAsk]);

  /**
   * THE READING ON SCREEN KEEPS ITS AGE HONEST (the RUN 85 frontend review). While the fields hold
   * a reading, ONE local timer is due at whichever comes first: the moment the age label would
   * turn, or just past the three-minute mark. At the first it redraws; at the second the reading
   * is withdrawn, held or not. No network, and no timer at all while the fields hold no reading.
   */
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const age = clockAgeMs(stampFields, localNow());
    if (age === null) return undefined;
    if (age > LIVE_CLOCK_FRESH_MS) {
      setStampFields((current) => expireClockStamp(current, localNow()));
      return undefined;
    }
    const timer = setTimeout(
      () => {
        setClockTick((count) => count + 1);
      },
      Math.min(liveClockAgeLabelChangesInMs(age), LIVE_CLOCK_FRESH_MS - age + 1),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [stampFields, clockTick]);

  // Back on this page - the phone was locked or pocketed. Whatever aged while it was away is judged
  // now (a timer may have slept with the phone) and redrawn, and if the next note has not begun,
  // the reading is withdrawn and a new one taken (`askClock`). An event, not a timer.
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState !== "visible") return;
      setStampFields((current) => expireClockStamp(current, localNow()));
      setClockTick((count) => count + 1);
      if (!noteBegun.current) askClock();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  /**
   * The current note's first touch. A tag, a keystroke or an umpire asks for a fresh reading -
   * that is the moment being stamped - and so withdraws the one on screen until it answers
   * (`askClock`); going to a stamp field asks for none, because the manager is supplying the
   * stamp themselves.
   */
  const beginNote = (withClock: boolean): void => {
    if (noteBegun.current) return;
    noteBegun.current = true;
    if (withClock) askClock();
  };

  /**
   * The manager has gone to a stamp field (a focus). The note has begun - with no clock read, since
   * the manager is about to supply the stamp - and whatever the field holds is theirs for this
   * note, so no reading can change it under a finger (`heldStamp`). This also covers picking the
   * quarter a reading already chose, which changes nothing and so fires no `change`.
   */
  const holdStamp = (): void => {
    countEdit(true);
    beginNote(false);
    setStampFields((current) => heldStamp(current, localNow()));
  };

  /**
   * Draft rows toggled OFF at the gate. Default on — team-by-default (spec 03 §2.2, D4).
   *
   * Under ruling §H an unselected row is **not a decision about the note**: it simply is not
   * in this press, stays a Draft, and can be released on any later day. That is why this is
   * still local screen state and is cleared after a press — it describes a selection, not a
   * status, and the old `heldBack` name (which implied a permanent hold) is gone with the
   * behaviour it described.
   */
  const [unselected, setUnselected] = useState<readonly string[]>([]);
  const [releasing, setReleasing] = useState(false);
  const [receipt, setReceipt] = useState<ReleaseResult | null>(null);
  const [releaseError, setReleaseError] = useState<Error | null>(null);

  /**
   * The note whose text editor is open, if any — spec 03 §2.3. **One at a time**, and drawn
   * in the page flow beside its own row, exactly as the §H warning is: an editor that floated
   * over the list would hide the note it was editing, and two open at once would invite the
   * author to lose whichever they did not save.
   *
   * `editText` is the author's typing and is NOT reset by a failed save — a save that did not
   * land must leave the words on screen, because they exist nowhere else.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<Error | null>(null);

  /** The Locked note whose §H warning is open, if any. One at a time, beside its own row. */
  const [confirmUnlock, setConfirmUnlock] = useState<string | null>(null);
  /** The note currently being locked, so its own button can say so without freezing others. */
  const [locking, setLocking] = useState<string | null>(null);
  const [lockError, setLockError] = useState<Error | null>(null);

  /**
   * THE 15-MINUTE UNDO (TD authorisation, 9 September 2026 — spec 11 §2's dated amendment).
   *
   * `undoing` is the id of the note whose undo is in flight, so the pressed row says `Undoing…`
   * and the others stay pressable. `undoError` is rendered IN THE FLOW beside the released list,
   * never as a toast: the most likely failure is a window that closed while the author was
   * deciding, and that is a sentence they have to be able to read at their own pace.
   */
  const [undoing, setUndoing] = useState<string | null>(null);
  const [undoError, setUndoError] = useState<Error | null>(null);

  const queue = useQueueStatus();

  const umpires =
    roster.status === "ready"
      ? roster.data.officials.filter((official) => official.role === "Umpire")
      : [];

  /**
   * The dropdown's options — the appointed four when there are any, the whole Umpire panel
   * otherwise.
   *
   * **`umpires` above is NOT narrowed and must not be**, because it is also how a queued note
   * finds the name to draw beside itself while it waits to be sent. A note already saved about
   * an umpire who is not on this match — captured before an appointment was corrected, or moved,
   * or simply typed by a manager watching a neighbouring pitch — renders in the list below from
   * `note.umpireMediaName`, which the server sends with the row and which nothing here filters.
   * Narrowing the roster itself would have blanked those names on exactly the notes that most
   * need explaining.
   *
   * The three branches, plainly: `ready` with rows narrows; `ready` with none is *"nobody has
   * been appointed"* and shows everybody; `loading` and `error` show everybody too — the first
   * so the control is usable the moment the roster lands rather than after two round trips, the
   * second because capture must survive an outage.
   */
  const appointedUmpires: readonly AppointedUmpireBody[] =
    appointed.status === "ready" ? appointed.data.umpires : [];
  const narrowed = appointedUmpires.length > 0;

  const fromServer = notes.status === "ready" ? notes.data.notes : [];
  const acknowledged = new Set(fromServer.map((note) => note.id));

  /**
   * The author's notes on this match: the server's rows, plus the captures the server has not
   * acknowledged, taken from the queue.
   *
   * **The server's row wins the moment there is one** — it is the system of record
   * (DATA-REVIEW DR5), and it carries the visibility a replay may have discovered has moved
   * on. The queue only fills the gap before that row exists, and it fills it whether or not
   * the list fetch itself succeeded: with no signal, `GET /notes/mine` fails and a note
   * captured thirty seconds ago must still be on screen.
   */
  const queuedRows = queue.queuedCaptures
    .filter((write) => write.note.matchId === matchId && !acknowledged.has(write.noteId))
    .map((write) =>
      noteFromQueue(
        write,
        umpires.find((umpire) => umpire.tmsPersonId === write.note.umpireId)?.mediaName ?? null,
      ),
    );

  const own = [...fromServer, ...queuedRows].sort((left, right) =>
    left.capturedAt.localeCompare(right.capturedAt),
  );
  const drafts = own.filter((note) => note.visibility === "draft");
  const released = own.filter((note) => note.visibility === "released");
  const locked = own.filter((note) => note.visibility === "locked");

  /**
   * THE COUNTDOWN'S TICK, AND WHY IT IS STATE RATHER THAN A `Date.now()` READ AT RENDER.
   *
   * React re-renders when state changes, not when time passes, so a label computed from
   * `Date.now()` in the body of this component would freeze at whatever minute the last
   * unrelated re-render happened in — and a frozen countdown beside a live deadline is a screen
   * lying about how long the author has.
   *
   * The interval runs ONLY while at least one released row still has an open window, and it
   * stops as soon as the last one closes: a screen full of long-released notes does no work at
   * all. One second rather than one minute because the label names the last minute
   * (`Less Than 1 Min`) and the control has to disappear on time rather than up to 59 seconds
   * late.
   */
  const [tick, setTick] = useState(() => Date.now());
  const undoableCount = released.filter((note) =>
    undoWindowIsOpen(note.releasedAt, new Date(tick)),
  ).length;
  useEffect(() => {
    if (undoableCount === 0) return undefined;
    const timer = setInterval(() => {
      setTick(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [undoableCount]);

  /**
   * RUN 78 — landing on the one note a `#note-<id>` fragment names.
   *
   * The Technical Delegate, live-testing on 2026-09-09: *"could you make all the notes
   * clickable so I don't always have to go into each match summary to view these individual
   * notes."* My Notes now opens a note in place, and the one link inside that expansion comes
   * here — so arriving on this screen has to put the reader in front of the row they pressed,
   * not merely on the match that holds it. A capture screen can carry three sections and a
   * long list, and a note that is on it but off-screen is, to the reader, a link that did
   * nothing.
   *
   * **The notes arrive asynchronously, so this cannot be a mount-only effect.** The first
   * commit has no rows at all; the row the fragment names exists only after `GET /notes/mine`
   * lands, or after the queue supplies it offline. The effect is therefore keyed on the ids
   * actually rendered and re-runs as they change, taking the first render in which the
   * element exists. The ref is what makes that safe: one landing per fragment, so a later
   * re-read — a capture, an edit, a release — re-renders the list without dragging the reader
   * back up to a note they have already been shown.
   *
   * Every other outcome is a no-op by construction: no hash, a malformed one, or a hash
   * naming a note that is not on this match all leave the screen exactly as it was.
   */
  const noteIdsRendered = own.map((note) => note.id).join(" ");
  const landedOn = useRef<string | null>(null);
  useEffect(() => {
    const wanted = noteIdFromHash(window.location.hash);
    if (wanted === null || landedOn.current === wanted) return;
    const target = document.getElementById(`note-${wanted}`);
    // Not here — not yet, or not on this match at all. A later render tries again; if the
    // note belongs to another match, no later render ever finds it and nothing happens.
    if (target === null) return;
    landedOn.current = wanted;
    pulseElement(target);
  }, [noteIdsRendered]);

  /**
   * Whether the note the confirmation is about has actually been read back.
   *
   * A 2xx is the server saying it took the write; this is the screen checking that claim against
   * the list it re-read afterwards, which is the same list the author would scroll to. Three
   * answers, and the mark says which:
   *
   *  * `checking` — the re-read is still in flight. The mark confirms the save and claims
   *    nothing about the list, because it does not know yet.
   *  * `present` — the note is in the list below. This is the only state that says so.
   *  * `unverified` — the re-read failed, or landed without the note. `Saved ✓` stands, because
   *    the receipt did; what is withdrawn is the claim about the list.
   */
  const savedCheck: "checking" | "present" | "unverified" =
    saving.status !== "saved"
      ? "checking"
      : notes.status === "ready"
        ? own.some((note) => note.id === saving.noteId)
          ? "present"
          : "unverified"
        : notes.status === "error"
          ? "unverified"
          : "checking";

  /**
   * Exactly what the Release press will name. Nothing else is touched by it.
   *
   * **A note with ANY press already in the queue is excluded and cannot be selected.** Both
   * cases are the same mistake — a second decision stacked behind an undelivered first — and
   * they are excluded together because the row already renders them together, under the mark
   * that says which press is in flight:
   *
   *  * `Lock` queued — the author has already said "this stays mine". The server has simply
   *    not heard it yet, and a gate press that swept the note up would use the queue's own
   *    latency to overturn a decision the author made deliberately.
   *  * `Release` queued — the press has been made and is waiting. Leaving the note selectable
   *    left the `Release To UM Team` button live over it, so a second press enqueued a
   *    duplicate release naming the same notes: a second `Release Queued` receipt for a press
   *    that will move nothing, and the same write listed twice in the sync sheet. The screen
   *    would have been reporting an action it was not going to take, which is the one thing
   *    spec 10 §1 exists to prevent. (Found by adversarial QA, 2026-08-13.)
   *
   * The exclusion is what disables the button, so the guard is in the state the button reads
   * rather than in a second condition on the press — there is one definition of "what this
   * press names", and it is this one.
   */
  const inFlight = (noteId: string): boolean =>
    queue.queuedLockNoteIds.includes(noteId) || queue.queuedReleaseNoteIds.includes(noteId);

  const selectedDrafts = drafts.filter(
    (note) => !unselected.includes(note.id) && !inFlight(note.id),
  );

  /**
   * The languages this manager may capture in, and the one she is configured as.
   *
   * Empty until the read lands, and empty for ever if it fails — see `languages` above. The
   * composer is English-only in that case, which is the state this screen was in before run 60.
   */
  const languageChoices = languages.status === "ready" ? languages.data.choices : [];
  const preferredLanguage = languages.status === "ready" ? languages.data.language : "English";
  /** The language a native capture will be filed under: the one picked, or the configured one. */
  const composerLanguage =
    nativeLang === null
      ? undefined
      : (languageByCode(nativeLang) ?? languageByName(preferredLanguage));

  // A tag alone is a complete observation (Q-02b), and so is a line of text. Neither is not.
  //
  // **A NATIVE ORIGINAL IS TEXT.** A note captured in another language arrives with no English
  // at all — the English is made afterwards, by Call 2 — so what makes a native capture saveable
  // is the original, exactly as an English capture's is its English.
  const canSave =
    nativeLang === null
      ? bodyEn.trim() !== "" || tagIds.length > 0
      : bodyNative.trim() !== "" || tagIds.length > 0;

  /**
   * The next note has begun, so the last note's confirmation has been read or will never be.
   *
   * Only the `saved` mark is cleared. The **queued** mark is untouched on purpose: it is not a
   * confirmation of a finished act but a standing statement about where a note IS, and it stops
   * being true when the queue drains, not when its author starts typing again. The `failed`
   * banner is untouched for the same reason — it is the only record that words were lost.
   */
  const clearSavedMark = (): void => {
    setSaving((current) => (current.status === "saved" ? { status: "idle" } : current));
  };

  const toggleTag = (tagId: string): void => {
    clearSavedMark();
    countEdit(false);
    beginNote(true);
    setTagIds((current) =>
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : // Two chips in quick succession attach to the SAME note (spec 02 §2.4).
          [...current, tagId],
    );
  };

  const save = (): void => {
    if (!canSave || saving.status === "saving" || minuteRefused) return;
    setSaving({ status: "saving" });

    // RUN 85 - a reading past its three minutes is sent as no stamp at all, even when the phone
    // slept through the timer that would have withdrawn it; and it leaves the fields with it.
    const stamp = expireClockStamp(stampFields, localNow());
    if (stamp !== stampFields) setStampFields((current) => expireClockStamp(current, localNow()));
    const quarterValue = stamp.quarter === "" ? null : (Number(stamp.quarter) as 1 | 2 | 3 | 4);
    const minuteValue = stamp.minute === "" ? null : Number(stamp.minute);
    // What the composer held at this press - so the reply, which can land seconds later, resets
    // what is still exactly this and nothing the manager has done since (the frontend review).
    const pressed = {
      edits: composerEdits.current.any,
      stampEdits: composerEdits.current.stamp,
      bodyEn,
      bodyNative,
      tagIds,
    };
    // Minted before the call rather than inside it, so the confirmation below can name the note
    // it is confirming and check the re-read for that id and no other.
    const noteId = newNoteId();

    // THE SINGLE WRITE SEAM. The id is generated here, on this device, and travels in the
    // path — so this note is fully identified before it has ever been sent, and a retry of
    // the identical call is a no-op on the server rather than a second note (spec 10 §2.3).
    captureNote(noteId, {
      umpireId: umpireId === "" ? null : umpireId,
      competition: competitionId,
      matchId,
      quarter: quarterValue,
      matchMinute: minuteValue,
      // Always provisional, pre-filled from the relayed TMS clock or typed (RUN 85): the reading
      // lags TMS, the manager may have corrected it, and spec 02 §2.7's correction path - the
      // one thing that would set this false - is not built. Point 3 of the module note.
      minuteProvisional: true,
      // A native capture sends NO English. It is not a blank the author forgot to fill in: the
      // English is Call 2's answer, made from the original on the review surface, and a note
      // that arrived with both would be asserting a translation nobody made.
      bodyEn: nativeLang === null ? bodyEn.trim() : "",
      // The pair, or neither — the API refuses one without the other. `nativeLang` is the code
      // as `PREFERRED_LANGUAGES` spells it, taken from the picker rather than from free text.
      ...(nativeLang === null
        ? {}
        : { bodyNative: bodyNative.trim(), nativeLang }),
      tagIds: [...tagIds],
      // Spec 10 §2.1's `meta` record, minted once and kept beside the queue — it identifies
      // which device wrote a note, and it now survives a reload.
      deviceId: deviceId(),
      capturedAt: new Date().toISOString(),
    }).then(
      (outcome) => {
        // THE HONESTY BRANCH. `sent` is the only thing that earns the word `Saved`; a queued
        // note says where it actually is. There is no shape of this call that lets the two be
        // printed the same way (`WriteOutcome`).
        setSaving(
          outcome.sent
            ? {
                status: "saved",
                noteId,
                stamp: stampOf(quarterValue, minuteValue, false),
              }
            : { status: "queued" },
        );
        // The words and the chips reset after the note is committed, ready for the next
        // observation (spec 02 §2.4) - but ONLY where they are still exactly what this press sent
        // (RUN 85, the frontend review). The inputs stay live while `Saving…` shows, and the reply
        // can land seconds later: words typed or a chip tapped in between belong to the next note
        // and are kept, where they used to be wiped. The umpire never resets: the next
        // observation is usually about the same umpire, moments later.
        setBodyEn((current) => (current === pressed.bodyEn ? "" : current));
        setBodyNative((current) => (current === pressed.bodyNative ? "" : current));
        setTagIds((current) => (sameIds(current, pressed.tagIds) ? [] : current));
        if (composerEdits.current.stamp !== pressed.stampEdits) {
          // The manager went to the stamp while this note was saving: that stamp is the NEXT
          // note's, it is theirs, and nothing the clock says replaces it.
          noteBegun.current = true;
        } else {
          // The next note starts from the clock: an edit made for this note stops sticking, the
          // clock's values are withdrawn and the clock is read again (`askClock`). If anything
          // else was touched since the press, the next note has already begun and this read is
          // its first-touch read. Queued or sent alike - a note waiting on the phone is finished.
          noteBegun.current = composerEdits.current.any !== pressed.edits;
          setStampFields(stampForNextNote);
          askClock();
        }
        // Only a delivered note is on the server, so only a delivered note is worth re-reading
        // for. A queued one appears from the queue itself, and a doomed fetch with no signal
        // would only replace the list with an error banner.
        if (outcome.sent) setReloads((count) => count + 1);
      },
      (error: unknown) => {
        setSaving({
          status: "failed",
          error: error instanceof Error ? error : new Error("The note was not saved."),
        });
      },
    );
  };

  /**
   * ONE release helper for both presses, because there is one release path in the API and
   * there must be one here. `unlock` is empty for the gate press over Drafts, and carries the
   * note's own id for the §H unlock-and-release — the acknowledgement is per note, so the
   * component can never acknowledge a note the author did not answer the warning about.
   */
  const sendRelease = (noteIds: readonly string[], unlock: readonly string[]): void => {
    if (releasing || noteIds.length === 0) return;
    setReleasing(true);
    setReleaseError(null);

    releaseMatchNotes(matchId, noteIds, unlock).then(
      (outcome) => {
        setReleasing(false);
        // A queued press has no receipt and is never given one. Spec 03 §4 and spec 10 §5
        // step 5 both name the string it gets instead, and it says `Queued`, not `Released`.
        setReceipt(
          outcome.sent
            ? { kind: "sent", body: outcome.receipt }
            : { kind: "queued", count: noteIds.length },
        );
        setUnselected([]);
        setConfirmUnlock(null);
        if (outcome.sent) setReloads((count) => count + 1);
      },
      (error: unknown) => {
        setReleasing(false);
        setReleaseError(error instanceof Error ? error : new Error("The release did not send."));
      },
    );
  };

  /** The gate press: exactly the selected Drafts. Nothing is acknowledged, because a Draft
   *  has nothing to unlock — and nothing left behind is locked. */
  const release = (): void => {
    sendRelease(
      selectedDrafts.map((note) => note.id),
      [],
    );
  };

  /** The Lock button. One press, no dialogue — see the module note. Idempotent server-side,
   *  so a double tap is one lock. */
  const lock = (noteId: string): void => {
    if (locking !== null) return;
    setLocking(noteId);
    setLockError(null);

    lockNote(noteId).then(
      (outcome) => {
        setLocking(null);
        // A queued lock leaves the note in `Draft` — because that is where the server still
        // has it — with its own `Lock Queued` mark, and out of the release selection. The
        // list is not re-read: there is nothing new on the server to read.
        if (outcome.sent) setReloads((count) => count + 1);
      },
      (error: unknown) => {
        setLocking(null);
        setLockError(error instanceof Error ? error : new Error("The note was not locked."));
      },
    );
  };

  /**
   * `Undo Release` — the author takes back a release, within fifteen minutes.
   *
   * **THE SERVER DECIDES, AND THE SCREEN REPORTS.** This call carries no time and no target
   * state; it may be refused with `note_undo_window_closed` even though the countdown on this
   * phone still showed a minute left, and when it is, the message is shown in the flow and the
   * list is re-read so the row goes back to reading `Released` with no control on it.
   *
   * **NOT QUEUED**, for the reason `client.ts` gives at length: an undo replayed an hour later
   * would take back a release its author had long since accepted. A failure is therefore a
   * failure the author is TOLD about, not a promise held in a queue.
   *
   * The list is re-read on success rather than patched: the note's new state is `draft` or
   * `locked` depending on where it came from, and the screen must show what the server holds
   * rather than what this phone assumed.
   */
  const undoRelease = (noteId: string): void => {
    if (undoing !== null) return;
    setUndoing(noteId);
    setUndoError(null);

    unreleaseNote(noteId).then(
      () => {
        setUndoing(null);
        setReloads((count) => count + 1);
      },
      (error: unknown) => {
        setUndoing(null);
        setUndoError(
          error instanceof Error ? error : new Error("The release was not taken back."),
        );
        // Re-read either way. The commonest failure is a window that closed, and after one the
        // row must stop offering a control that cannot work.
        setReloads((count) => count + 1);
      },
    );
  };

  /**
   * The `Undo Release` affordance, for a **Released** row inside its window and for no other.
   *
   * It returns `null` the instant the window closes, so an expired row carries NO control —
   * not a disabled one, on the same reasoning the module note gives for there being no greyed
   * `Edit` on a released row: a control that looks like it might work under some condition
   * teaches the author that a rule has exceptions it does not have.
   *
   * The remaining time is drawn beside it, rounded UP to the whole minute and naming the last
   * minute rather than counting it (`undoWindowLabel`), so the label never promises time that is
   * not there.
   */
  const undoButton = (note: OwnNoteView): React.JSX.Element | null => {
    const remaining = undoWindowRemainingMs(note.releasedAt, new Date(tick));
    const label = undoWindowLabel(remaining);
    if (label === null) return null;

    return (
      <span className="undorelease">
        <button
          type="button"
          className="gbtn gbtn--sm"
          disabled={undoing !== null}
          onClick={() => {
            undoRelease(note.id);
          }}
          data-testid={`undo-${note.id}`}
        >
          {undoing === note.id ? "Undoing…" : "Undo Release"}
        </button>
        {/* Quiet, and a fact rather than a warning: this is how long is left, not an alarm. */}
        <span className="undorelease__left" data-testid={`undo-left-${note.id}`}>
          {label} Left
        </span>
      </span>
    );
  };

  /**
   * EDITING A NOTE'S TEXT BEFORE RELEASE — spec 03 §2.3.
   *
   * The editor opens pre-filled with the note's CURRENT body, taken from the list the server
   * gave us, so the author edits what is stored rather than starting from a blank box.
   */
  const beginEdit = (note: OwnNoteView): void => {
    setEditing(note.id);
    setEditText(note.bodyEn);
    setEditError(null);
  };

  /** Cancel sends nothing, so the stored text is by definition untouched. */
  const cancelEdit = (): void => {
    setEditing(null);
    setEditText("");
    setEditError(null);
  };

  /**
   * Save. **Nothing on this screen changes until the server's reply lands** (honesty rule 1),
   * and what replaces it is a re-read of `GET /notes/mine` rather than the text this phone
   * sent — the list must show what is stored, not what was typed.
   *
   * A FAILURE KEEPS THE EDITOR OPEN WITH THE TYPING IN IT. There is no offline queue behind
   * this call (see the module note), so a failed save means the words exist only in this
   * textarea, and closing it would destroy them. The failure line says so in the flow, beside
   * the note it concerns.
   */
  const saveEdit = (noteId: string): void => {
    if (savingEdit) return;
    setSavingEdit(true);
    setEditError(null);

    editNoteText(noteId, editText.trim()).then(
      () => {
        setSavingEdit(false);
        setEditing(null);
        setEditText("");
        // The server is the system of record (DATA-REVIEW DR5): re-read rather than patch
        // the row in place, so the text on screen is the text that was stored.
        setReloads((count) => count + 1);
      },
      (error: unknown) => {
        setSavingEdit(false);
        setEditError(
          error instanceof Error ? error : new Error("The edit was not saved."),
        );
      },
    );
  };

  /**
   * The `Edit` affordance, for a **Draft** or a **Locked** row and for no other. It is not
   * rendered at all on a Released row — see the module note; a disabled control there would
   * imply that editing a released note is a thing that could happen.
   *
   * Withheld while a press for this row is already in the queue, on the Lock button's
   * reasoning: an edit stacked behind an undelivered release would rewrite the text of a note
   * that is on its way to the panel, and which of the two the author remembers doing is not a
   * question the screen should be able to raise.
   */
  /**
   * `Review Translation` — the way into `NativeNoteReview`, and it appears on a native note and
   * on no other. Spec 08 §3: *"this screen is the review step, reached automatically when the
   * note is opened for review or at the release gate"*, and *"every note captured in a
   * non-English language must pass through this screen before it can be released."*
   *
   * The API enforces the second half whatever this screen does — a release naming an unreviewed
   * native note is refused — so this button is the way TO the gate rather than the gate itself.
   *
   * Withheld while a press for this row is already in the queue, on the Edit button's reasoning.
   */
  const reviewButton = (note: OwnNoteView): React.JSX.Element | null => {
    if (note.bodyNative === null || inFlight(note.id) || reviewing === note.id) return null;
    return (
      <button
        type="button"
        className="gbtn gbtn--sm"
        data-testid={`review-native-${note.id}`}
        disabled={editing !== null}
        aria-label="Review the English version of this note"
        onClick={() => {
          setReviewing(note.id);
          setEditing(null);
        }}
      >
        Review Translation
      </button>
    );
  };

  /** The review surface, in the page flow beside its row — the §H warning's own pattern. */
  const nativeReview = (note: OwnNoteView): React.JSX.Element | null => {
    if (reviewing !== note.id) return null;
    return (
      <NativeNoteReview
        note={note}
        releasing={releasing}
        onChanged={() => {
          setReloads((count) => count + 1);
        }}
        onRelease={(noteId) => {
          // ONE release path, in this component, with one receipt and one failure line — the
          // same reason the API keeps one release route. The review surface asks; it does not
          // release on its own. `unlock` carries the id when the note is Locked, because
          // releasing a locked note unlocks it and that is acknowledged per note (§H).
          sendRelease([noteId], note.visibility === "locked" ? [noteId] : []);
          setReviewing(null);
        }}
        onClose={() => {
          setReviewing(null);
        }}
      />
    );
  };

  const editButton = (note: OwnNoteView): React.JSX.Element | null => {
    if (inFlight(note.id) || editing === note.id) return null;
    return (
      <button
        type="button"
        className="gbtn gbtn--sm"
        disabled={editing !== null}
        aria-label={`Edit this note`}
        onClick={() => {
          beginEdit(note);
        }}
      >
        Edit
      </button>
    );
  };

  /**
   * The editor itself, in the page flow beside its row.
   *
   * Written as a function returning elements rather than as a component, deliberately: a
   * component declared inside this render is a NEW component type on every render, so React
   * would unmount and remount the textarea between keystrokes and the caret would jump.
   */
  const noteEditor = (note: OwnNoteView): React.JSX.Element | null => {
    if (editing !== note.id) return null;

    // "A note needs either text or at least one tap-tag" (Q-02b) — the rule capture enforces,
    // said here before the author presses rather than only in the API's refusal. The tags are
    // fixed at capture, so a tag-less note's text cannot be emptied at all.
    const wouldBeEmpty = editText.trim() === "" && note.tagIds.length === 0;

    return (
      <div className="noteedit" data-testid={`note-edit-${note.id}`}>
        <label className="field">
          <span className="field__label">Edit This Note</span>
          <textarea
            value={editText}
            rows={3}
            aria-label={`Edit the text of this note`}
            onChange={(event) => {
              setEditText(event.target.value);
            }}
          />
        </label>
        {wouldBeEmpty ? (
          <p className="btnhint">
            A Note Needs Either Text Or At Least One Tag — This One Has No Tag, So It Cannot Be
            Left Empty.
          </p>
        ) : null}
        <div className="noteedit__acts">
          <button
            type="button"
            className="gbtn gbtn--sm gbtn--solid"
            data-testid={`note-edit-save-${note.id}`}
            disabled={savingEdit || wouldBeEmpty}
            onClick={() => {
              saveEdit(note.id);
            }}
          >
            {/* `Saving…` and not `Saved`: the word `Saved` is earned by a reply, never by a
                press (spec 02 §2.5, honesty rule 1). */}
            {savingEdit ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="gbtn gbtn--sm"
            data-testid={`note-edit-cancel-${note.id}`}
            disabled={savingEdit}
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </div>
        {editError !== null ? (
          <p className="noteedit__fail" role="status">
            {`Not Saved — ${editError.message} Your Words Are Still Here. Try Again When You Have Signal.`}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <section className="card" aria-labelledby="capture-heading">
        <div className="card__head">
          <span className="card__kicker" id="capture-heading">
            Capture
          </span>
          <span className="card__note num">{`Match ${String(matchId)}`}</span>
        </div>

        {roster.status === "loading" ? <Skeleton rows={3} /> : null}
        {roster.status === "error" ? <FailBanner error={roster.error} /> : null}

        <label className="field">
          <span className="field__label">Umpire</span>
          <select
            value={umpireId}
            onChange={(event) => {
              // The next observation has begun — see `clearSavedMark`, and `StampFields`.
              clearSavedMark();
              countEdit(false);
              beginNote(true);
              setUmpireId(event.target.value);
            }}
          >
            {/* Spec 01 §2.8: a note that is not about one umpire is legitimate and keeps
                its match. It is a general note, not an unattributed one. It stays on the
                narrowed list too — the reason a note has no named umpire has nothing to do
                with who was appointed. */}
            <option value="">Not About One Umpire</option>
            {narrowed
              ? appointedUmpires.map((umpire) => (
                  <option key={umpire.tmsPersonId} value={umpire.tmsPersonId}>
                    {appointedLabel(umpire)}
                  </option>
                ))
              : umpires.map((umpire) => (
                  <option key={umpire.tmsPersonId} value={umpire.tmsPersonId}>
                    {`${umpire.mediaName} · ${umpire.country}`}
                  </option>
                ))}
          </select>
        </label>

        {/*
         * THE COMPOSER, IN ONE LANGUAGE OR THE OTHER — spec 08 §3.
         *
         * One box, not two. A note is written in one language, and a screen offering an English
         * box and a native box at once would invite a manager to fill in both — which would
         * assert a translation nobody made and put a sentence in front of the panel that no AI
         * call and no review ever touched.
         */}
        {composerLanguage === undefined ? (
          <label className="field" data-howto="capture-form">
            <span className="field__label">Type Or Dictate Your Note</span>
            <span className="field__sub">
              Uses Your Keyboard&rsquo;s Mic · No Audio Is Recorded
            </span>
            <textarea
              value={bodyEn}
              rows={3}
              placeholder="Describe what you saw, not what you conclude."
              onChange={(event) => {
                clearSavedMark();
                countEdit(false);
                beginNote(true);
                setBodyEn(event.target.value);
              }}
            />
          </label>
        ) : (
          <label className="field" data-howto="capture-form">
            <span className="field__label">{`Write In ${composerLanguage.name}`}</span>
            {/*
             * The two things a manager needs to know before typing, and neither is softened.
             * There is no microphone here either (decision B3): the keyboard's mic key produces
             * text, and this application never sees, stores or sends audio in any language.
             */}
            <span className="field__sub">
              Uses Your Keyboard&rsquo;s Mic · No Audio Is Recorded · The English Is Made
              Afterwards, And You Correct It Before Anyone Sees It
            </span>
            <textarea
              className="natrev__native"
              // §2.3's per-script font stack, from the language table rather than a class per
              // language — an eighth language is a row, not a stylesheet.
              style={{ fontFamily: composerLanguage.fontStack }}
              value={bodyNative}
              rows={3}
              aria-label={`Write this note in ${composerLanguage.name}`}
              onChange={(event) => {
                clearSavedMark();
                countEdit(false);
                beginNote(true);
                setBodyNative(event.target.value);
              }}
            />
          </label>
        )}

        {/*
         * `Write In Another Language` — spec 08 §3's composer switch, *"which is also how a
         * bilingual UM handles the occasional note"*.
         *
         * It is a plain button rather than an overflow menu: this composer has one other control
         * and a menu holding a single item is a menu nobody opens. It appears only when the
         * language list has actually loaded, because a switch with nothing to switch to is a
         * control that cannot be used.
         */}
        {languageChoices.length > 0 ? (
          <div className="natswitch">
            {composerLanguage === undefined ? (
              <button
                type="button"
                className="gbtn gbtn--sm"
                data-testid="write-in-another-language"
                onClick={() => {
                  // Default to the manager's own configured language, which is the whole point
                  // of decision L2's field — and to the first non-English choice when she is
                  // configured as English, so the picker opens on something usable.
                  const own = languageByName(preferredLanguage);
                  setNativeLang(
                    own !== undefined && own.name !== "English"
                      ? own.code
                      : (languageChoices.find((choice) => choice.name !== "English")?.code ??
                        null),
                  );
                }}
              >
                Write In Another Language
              </button>
            ) : (
              <>
                <label className="field field--inline">
                  <span className="field__label">Language</span>
                  <select
                    value={nativeLang ?? ""}
                    aria-label="The language this note is written in"
                    onChange={(event) => {
                      setNativeLang(event.target.value);
                    }}
                  >
                    {languageChoices
                      .filter((choice) => choice.name !== "English")
                      .map((choice) => (
                        <option key={choice.code} value={choice.code}>
                          {`${choice.endonym} · ${choice.name}`}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="gbtn gbtn--sm"
                  data-testid="write-in-english"
                  onClick={() => {
                    setNativeLang(null);
                    // The typing is kept, not discarded: switching back is not a decision to
                    // throw away what was written, and the words exist nowhere else.
                  }}
                >
                  Write In English Instead
                </button>
              </>
            )}
          </div>
        ) : null}

        <div className="tagpal" data-howto="capture-tags">
          <div className="tagpal__head">
            <span className="card__kicker">Or Tap A Tag</span>
            <span className="card__note">≈3 Seconds</span>
          </div>
          <div className="tagpal__chips">
            {CAPTURE_TAP_TAGS.map((tag) => {
              const on = tagIds.includes(tag.tag_id);
              return (
                <button
                  key={tag.tag_id}
                  type="button"
                  className={on ? "tag tag--on" : "tag"}
                  aria-pressed={on}
                  onClick={() => {
                    toggleTag(tag.tag_id);
                  }}
                >
                  {tag.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="stamprow">
          <label className="field field--inline">
            <span className="field__label">Quarter</span>
            <select
              value={quarter}
              aria-describedby={describedBy(clockAgeNow === null ? null : STAMP_SOURCE_ID)}
              onFocus={holdStamp}
              onChange={(event) => {
                clearSavedMark();
                // The manager's own stamp: it sticks for this note, and asks for no reading.
                countEdit(true);
                beginNote(false);
                const value = event.target.value;
                setStampFields((current) => typedStamp(current, { quarter: value }));
              }}
            >
              <option value="">—</option>
              <option value="1">Q1</option>
              <option value="2">Q2</option>
              <option value="3">Q3</option>
              <option value="4">Q4</option>
            </select>
          </label>
          <label className="field field--inline">
            <span className="field__label">Minute</span>
            <input
              type="number"
              min={0}
              value={matchMinute}
              // The refusal is linked, not only shown (the frontend re-review): a screen reader on
              // this field hears why it is refused, as its description and as its error message.
              aria-describedby={describedBy(
                clockAgeNow === null ? null : STAMP_SOURCE_ID,
                minuteRefused ? MINUTE_REFUSED_ID : null,
              )}
              aria-invalid={minuteRefused ? true : undefined}
              aria-errormessage={minuteRefused ? MINUTE_REFUSED_ID : undefined}
              onFocus={holdStamp}
              onChange={(event) => {
                clearSavedMark();
                countEdit(true);
                beginNote(false);
                const value = event.target.value;
                setStampFields((current) => typedStamp(current, { minute: value }));
              }}
            />
          </label>
        </div>
        {/*
         * RUN 85 - where the stamp came from, while it came from the clock: the reading's age as it
         * is NOW, so the manager can add the minutes the relay has not caught up with. Gone the
         * moment either field is edited - the stamp is then the manager's, and a line crediting the
         * clock would be claiming their number.
         *
         * THE SLOT IS ALWAYS DRAWN, EXACTLY ONE LINE TALL, and only its words come and go (both
         * frontend reviews): a line that mounted, unmounted or wrapped moved `Save This Note` under a
         * thumb on its way to it. The words never wrap; see `clockSourceLine` for what gives way.
         */}
        <p className="stampslot" data-testid="stamp-slot">
          {clockAgeNow === null ? null : (
            <span className="stampsrc" id={STAMP_SOURCE_ID} data-testid="stamp-source">
              <span className="stampsrc__from">{CLOCK_SOURCE_FROM}</span>{" "}
              <span className="stampsrc__age">{clockSourceAge(clockAgeNow)}</span>
            </span>
          )}
        </p>
        {/*
         * A minute the note may not carry, refused here in words rather than by the API afterwards
         * - which takes no fraction, and would store any whole number, however large.
         */}
        {minuteRefused ? (
          <p className="stamprefuse" role="alert" id={MINUTE_REFUSED_ID} data-testid="minute-refused">
            {MINUTE_REFUSED}
          </p>
        ) : null}
        <p className="field__sub">
          The Stamp Is A Memory-Jogger, Not A Record — Correct It If It Looks Wrong.
        </p>

        {/*
         * THERE IS NO `Keep Private` CHECKBOX HERE, AND ONE MUST NOT BE ADDED BACK.
         *
         * Ruling §H (D-5, 2026-08-12): "capture always creates a Draft — private by default
         * … locking is a deliberate act on an existing note, never a capture option." The
         * checkbox that used to sit here made a permanent decision in the moment of capture,
         * pitch-side, one mis-tap away — the hazard the ruling names. Locking is now the
         * `Lock` button on a saved note, below.
         */}

        <button
          type="button"
          className="gbtn gbtn--solid umsave"
          data-howto="capture-save"
          disabled={!canSave || saving.status === "saving" || minuteRefused}
          onClick={save}
        >
          {saving.status === "saving" ? "Saving…" : "Save This Note"}
        </button>

        {saving.status === "failed" ? <FailBanner error={saving.error} /> : null}

        {/*
         * Spec 02 §2.5: *"the word `Saved` must be truthful"*. It is used for a note the
         * server has acknowledged and for nothing else; a note that is only on the phone says
         * so, in the words spec 10 §4 gives it. Both are `role="status"`, because both are
         * the answer to a press the author just made.
         */}
        {/*
         * THE EXPLICIT CONFIRMATION, IN THE `savemark` POSITION BY THE BUTTON — the TD's
         * finding of 2026-09-09, and see the `Saving` type for the design and its reasons.
         *
         * It is a mark and not a toast: it sits where the author's thumb just was, it does not
         * float over anything, and it does not expire.
         */}
        {saving.status === "saved" ? (
          <p
            className={
              savedCheck === "unverified"
                ? "savemark savemark--confirmed savemark--unverified"
                : "savemark savemark--confirmed"
            }
            role="status"
            data-testid="save-confirmed"
          >
            {savedCheck === "present"
              ? `Saved ✓ · ${saving.stamp} · In Your Notes Below`
              : savedCheck === "unverified"
                ? `Saved ✓ · ${saving.stamp} — Pull Down If It Does Not Appear Below`
                : `Saved ✓ · ${saving.stamp}`}
          </p>
        ) : null}
        {saving.status === "queued" ? (
          <p className="savemark savemark--queued" role="status">
            Saved On This Phone · Waiting To Send
          </p>
        ) : null}

        {/*
         * DECISION L2's FIELD, WITH A CONTROL AT LAST — spec 08 §1: the whole feature is
         * *"driven by the per-UM Preferred Language field"*, and §3 makes it what decides
         * whether the composer opens in English or in the manager's own language.
         *
         * It sets the ACCOUNT preference, so it persists across devices and sessions; the picker
         * above it sets the language of THIS note and nothing else. Two controls because they
         * are two decisions, and a single one would make "one note in Korean" a permanent change
         * of language.
         *
         * There is no TD-facing view of this anywhere in the API and there must not be: a
         * panel-wide readout of who writes in what language is a per-manager readout of the
         * shape commitment C3 forbids, one inference from "who needs help with English".
         */}
        {languageChoices.length > 0 ? (
          <label className="field field--inline natswitch__pref">
            <span className="field__label">Your Preferred Language</span>
            <select
              value={preferredLanguage}
              disabled={languageSaving}
              aria-label="Your preferred language"
              onChange={(event) => {
                const wanted = event.target.value;
                setLanguageSaving(true);
                setPreferredLanguage(wanted).then(
                  () => {
                    setLanguageSaving(false);
                    // Re-read rather than patch in place: the control shows what the server
                    // stored, which is the rule every write on this screen follows.
                    setLanguageReloads((count) => count + 1);
                  },
                  () => {
                    // A failed save leaves the control showing the STORED value, which is
                    // still the old one — so the screen never claims a preference that was
                    // not saved. Nothing else on this screen depends on it.
                    setLanguageSaving(false);
                  },
                );
              }}
            >
              {languageChoices.map((choice) => (
                <option key={choice.code} value={choice.name}>
                  {`${choice.endonym} · ${choice.name}`}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {/*
         * RULING R4″ (Alfred, 2026-09-09). The first line is unchanged and still true of the
         * text. The second is the disclosure that arrived with the capability: the Technical
         * Delegate can count what is here, and cannot read it.
         */}
        <p className="card__foot">
          Private Draft Until You Release
          <span className="card__footnote">
            The TD Can See How Many Notes You Have Recorded, Pending And Released, And When You
            Last Recorded — Never What Your Drafts And Locked Notes Say
          </span>
        </p>
      </section>

      <section className="card" aria-labelledby="mynotes-heading" data-howto="my-notes">
        <div className="card__head">
          <span className="card__kicker" id="mynotes-heading">
            Your Notes On This Match
          </span>
          {/* The length of the author's own list, for the author's own eyes (ruling R4′). */}
          <span className="card__note num">{own.length}</span>
        </div>

        {notes.status === "loading" ? <Skeleton rows={3} /> : null}
        {notes.status === "error" ? <FailBanner error={notes.error} /> : null}

        {/* `ready` and not merely "not loading": with a failed fetch the screen does not know
            that nothing was captured, and must not say so. The banner above says what it does
            know. Queued captures count towards `own`, so this can never hide one. */}
        {notes.status === "ready" && own.length === 0 ? (
          <p className="card__body">
            No Notes To Review For This Match — Nothing Was Captured, Nothing Will Be Shared.
          </p>
        ) : null}

        {lockError !== null ? <FailBanner error={lockError} /> : null}

        {drafts.length > 0 ? (
          <>
            <div className="notelist__head">
              {/* §H's label, verbatim, beside spec 03 §2.2's own two strings. */}
              <span className="card__kicker">Draft</span>
              <span className="card__note">Share With The UM Team</span>
              <span className="card__note">Default On</span>
            </div>
            <ul className="notelist">
              {drafts.map((note) => {
                const off = unselected.includes(note.id);
                // ONE definition of "a press is already in the queue for this row", shared
                // with `selectedDrafts` above — so what the row hides and what the press
                // names can never drift apart again.
                const pressInFlight = inFlight(note.id);
                return (
                  /* RUN 78 — the anchor `Open In Match ›` lands on. Every row in all three
                     sections carries one, because a note is reached from My Notes whatever
                     state it is in. */
                  <li key={note.id} id={`note-${note.id}`} className="noterow">
                    <span className="noterow__nm">
                      {note.umpireMediaName ?? "General Note For This Match"}
                    </span>
                    <span className="noterow__min num">{stamp(note)}</span>
                    {pressInFlight ? (
                      /*
                       * The author has already pressed something on this row and the queue is
                       * carrying it. Offering the choice again would invite them to overturn
                       * their own decision by accident, using nothing but the latency of the
                       * network — so the mark stands in the toggle's place and says which
                       * press is in flight.
                       */
                      <span className="noterow__share">
                        <PendingMark noteId={note.id} queue={queue} />
                      </span>
                    ) : (
                      <label className="noterow__share">
                        <input
                          type="checkbox"
                          checked={!off}
                          aria-label={`Release this note to the UM team`}
                          onChange={(event) => {
                            setUnselected((current) =>
                              event.target.checked
                                ? current.filter((id) => id !== note.id)
                                : [...current, note.id],
                            );
                          }}
                        />
                        {/*
                         * `Not This Time` and NOT `Keep Private`. Under §H an unselected row
                         * stays a Draft and can be released on any later day; calling it
                         * private would promise a permanence the press no longer creates.
                         */}
                        <span>{off ? "Not This Time" : "Release"}</span>
                      </label>
                    )}
                    <span className="noterow__txt">{note.bodyEn}</span>
                    {pressInFlight ? null : (
                      <PendingMark noteId={note.id} queue={queue} />
                    )}
                    {note.tagIds.map((tagId) => (
                      <span key={tagId} className="noterow__tag">
                        {tagLabel(tagId)}
                      </span>
                    ))}
                    <div className="noterow__acts">
                      {/*
                       * THE EDIT BUTTON — spec 03 §2.3, "tappable to expand and edit text
                       * before release". A Draft is still being written, so its author may
                       * still write it.
                       */}
                      {editButton(note)}
                      {/*
                       * THE WAY INTO THE REVIEW SURFACE, on a native note only (spec 08 §3).
                       * An English note has nothing to review: its author wrote it.
                       */}
                      {reviewButton(note)}
                      {/*
                       * THE LOCK BUTTON — §H's explicit act, and the only way a note becomes
                       * Locked. One press, no confirmation: it is taken at the desk on a note
                       * already written, and it is not final — the note can still be
                       * released later, through the warned one-step path.
                       *
                       * It is withheld while this row already has a press in the queue: a
                       * second decision stacked behind an undelivered first is two
                       * contradictory instructions replayed in order, and the loser is
                       * whichever one the author remembers making.
                       */}
                      {pressInFlight ? null : (
                        <button
                          type="button"
                          className="gbtn gbtn--sm"
                          disabled={locking !== null}
                          aria-label={`Lock this note`}
                          onClick={() => {
                            lock(note.id);
                          }}
                        >
                          {locking === note.id ? "Locking…" : "🔒 Lock"}
                        </button>
                      )}
                    </div>
                    {noteEditor(note)}
                    {nativeReview(note)}
                  </li>
                );
              })}
            </ul>

            <button
              type="button"
              className="gbtn gbtn--solid releasebtn"
              data-howto="release-notes"
              disabled={releasing || selectedDrafts.length === 0}
              onClick={release}
            >
              {releasing ? "Releasing…" : "Release To UM Team"}
            </button>
            {/*
             * Two reasons the press can be unavailable, and they are NOT the same sentence.
             * "Nothing is selected" would be a lie when the truth is "you already pressed it
             * and it is on its way" — and it is the lie most likely to produce the second
             * press this guard exists to prevent.
             */}
            {selectedDrafts.length === 0 && drafts.every((note) => inFlight(note.id)) ? (
              <p className="btnhint">
                Already Pressed — Every Note Here Is Waiting To Send. Nothing Needs Pressing
                Again.
              </p>
            ) : selectedDrafts.length === 0 ? (
              <p className="btnhint">
                Nothing Is Selected — A Note Left Unselected Stays A Draft And Can Be Released
                Later.
              </p>
            ) : null}
            {/*
             * Spec 00 §3 **C1**, verbatim — and a deliberate departure from the mockup's
             * `Nothing Sent Until Release` (spec 03 §2.6 and §5).
             *
             * That string is not true of this build. A note is written to the tournament
             * server the moment it is captured (`PUT /notes/:id`); what waits for this press
             * is the note becoming visible to the other five managers and the TD — the
             * `visibility` transition, excluded at the data layer until then. "Nothing Sent"
             * would be a claim that the text is still on this phone, which is precisely the
             * device-locality overclaim §3's honesty constraint bars ("only on your device",
             * "nobody can ever see"). C1's own sanctioned sentence says the true thing and
             * says it in the spec's own words, so nothing is invented to replace it.
             */}
            <p className="card__foot">Nothing Is Shared Until You Press Release</p>
          </>
        ) : null}

        {releaseError !== null ? <FailBanner error={releaseError} /> : null}

        {receipt !== null ? (
          /*
           * The persistent receipt of spec 03 §2.4 step 4, named rather than counted. It
           * describes the caller's own press and nobody else's notes.
           *
           * `· N Kept Private` IS GONE (ruling §H). A press keeps nothing private: the notes
           * it did not name are still Drafts and no decision was made about them, so
           * reporting them as an outcome would be reporting a decision the author did not
           * take. What IS reported, when there is one, is the unlock — a decision they did.
           */
          <p className={receipt.kind === "sent" ? "receipt" : "receipt receipt--queued"} role="status">
            {receipt.kind === "sent"
              ? `Released — ${String(receipt.body.released.length)} ${
                  receipt.body.released.length === 1 ? "Note" : "Notes"
                } Now Visible To The Panel${
                  receipt.body.unlocked.length === 0
                    ? ""
                    : ` · ${String(receipt.body.unlocked.length)} Unlocked To Release`
                }${undoStillOffered(receipt.body.undoUntil, tick) ? UNDO_RECEIPT_SUFFIX : ""}`
              : releaseQueuedReceipt(receipt.count)}
          </p>
        ) : null}

        {released.length > 0 ? (
          <>
            <div className="notelist__head">
              <span className="card__kicker">Released</span>
              <span className="card__note">Visible To The Panel And The TD</span>
              {/*
               * §H consequence 5, AS THE TD'S AUTHORISATION OF 9 SEPTEMBER 2026 AMENDS IT, said
               * on the screen where it becomes true and said the same way whether or not any
               * row is currently inside its window. A heading that changed with the clock would
               * be a rule that appeared to come and go; the rule is constant and it is the
               * CONTROL that comes and goes.
               */}
              <span className="card__note">
                Final After 15 Minutes · Undo Is Available Until Then
              </span>
            </div>
            <ul className="notelist">
              {released.map((note) => (
                <li key={note.id} id={`note-${note.id}`} className="noterow noterow--released">
                  <span className="noterow__nm">
                    {note.umpireMediaName ?? "General Note For This Match"}
                  </span>
                  <span className="noterow__min num">{stamp(note)}</span>
                  <span className="noterow__txt">{note.bodyEn}</span>
                  {/* §4: the mark appears wherever the note renders, not only at capture. */}
                  <PendingMark noteId={note.id} queue={queue} />
                  {/*
                   * THE ONE CONTROL A RELEASED ROW EVER CARRIES, and it carries it only while
                   * the window is open (TD authorisation, 9 September 2026). After fifteen
                   * minutes `undoButton` returns null and the row reads exactly as a released
                   * row always has.
                   */}
                  {undoButton(note)}
                  {/*
                   * THERE IS NO `Edit` HERE, AND THERE MUST NOT BE — not even a disabled one.
                   * Spec 03 §2.3: "After release, notes are append-only: corrections append a
                   * superseding version rather than editing history" (DATA-REVIEW G11). A
                   * greyed-out Edit would tell the author that editing a released note is a
                   * thing that could happen under some condition, and there is no such
                   * condition: the API refuses it with a typed 409 and the database refuses it
                   * with a trigger (migration 046). A correction is a new note.
                   */}
                </li>
              ))}
            </ul>
            {/*
             * The undo's own failure line, in the flow beside the notes it is about — the house
             * pattern, never a toast. The likeliest failure is a window that closed while the
             * author was deciding, and that is a sentence they must be able to read at their own
             * pace rather than one that vanishes.
             */}
            {undoError !== null ? <FailBanner error={undoError} /> : null}
          </>
        ) : null}

        {locked.length > 0 ? (
          <>
            <div className="notelist__head">
              <span className="card__kicker">Locked</span>
              <span className="card__note">Never Shared · Still Writes Your Report</span>
            </div>
            <ul className="notelist">
              {locked.map((note) => (
                <li key={note.id} id={`note-${note.id}`} className="noterow noterow--locked">
                  <span className="noterow__nm">
                    {note.umpireMediaName ?? "General Note For This Match"}
                  </span>
                  <span className="noterow__min num">{stamp(note)}</span>
                  <span className="noterow__txt noterow__txt--muted">{note.bodyEn}</span>
                  <PendingMark noteId={note.id} queue={queue} />
                  <div className="noterow__acts">
                    {/*
                     * A LOCKED NOTE IS STILL EDITABLE (spec 03 §2.3). Locking says who may
                     * read a note; it says nothing about whether its author has finished
                     * writing it, and the note is still private to them either way. The API
                     * draws the same line — the cut-off is release, not the lock.
                     */}
                    {editButton(note)}
                    {/* A LOCKED native note is still reviewable: a lock says who may read a
                        note, never whether its author has finished writing it — and its way out
                        is the warned unlock-and-release, which still needs a reviewed English. */}
                    {reviewButton(note)}
                    {/*
                     * RELEASE ON A LOCKED NOTE (ruling §H). There is no `Unlock` control,
                     * here or anywhere: a Locked note leaves that state only by being
                     * released, and this button asks for exactly that. The warning is opened
                     * beside the row rather than fired at it, and the acknowledgement it
                     * produces travels per note in the release call itself — so the API, not
                     * only this screen, is what makes the warning unskippable.
                     */}
                    <button
                      type="button"
                      className="gbtn gbtn--sm"
                      disabled={releasing}
                      aria-label={`Release this locked note`}
                      onClick={() => {
                        setConfirmUnlock(note.id);
                        setReleaseError(null);
                      }}
                    >
                      Release
                    </button>
                  </div>

                  {noteEditor(note)}
                  {nativeReview(note)}

                  {confirmUnlock === note.id ? (
                    <div
                      className="lockconfirm"
                      role="dialog"
                      aria-modal="false"
                      aria-labelledby={`lockConfirm-${note.id}`}
                      data-testid="unlock-confirm"
                    >
                      {/* ALFRED'S WORDS, VERBATIM. Do not rephrase, retitle or sentence-case
                          differently — this string is the ruling. */}
                      <b id={`lockConfirm-${note.id}`}>{LOCKED_RELEASE_WARNING}</b>
                      <p className="lockconfirm__note">
                        Releasing It Unlocks It. Once Released It Is Visible To The Panel And
                        The TD, And It Can Never Be Unreleased.
                      </p>
                      <div className="lockconfirm__acts">
                        <button
                          type="button"
                          className="gbtn gbtn--solid"
                          data-testid="unlock-confirm-yes"
                          disabled={releasing}
                          onClick={() => {
                            // The acknowledgement is THIS note's id and no other: the author
                            // answered the warning about one note, so one note is unlocked.
                            sendRelease([note.id], [note.id]);
                          }}
                        >
                          {releasing ? "Releasing…" : "Yes, Unlock And Release"}
                        </button>
                        <button
                          type="button"
                          className="gbtn"
                          data-testid="unlock-confirm-no"
                          onClick={() => {
                            setConfirmUnlock(null);
                          }}
                        >
                          No, Keep It Locked
                        </button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    </>
  );
}
